import { Button } from '@/components/ui/button';
import {
  type CallTranscriptLine,
  type ConversationCall,
  useCallTranscript,
} from '@/features/voice-receptionist/api';
import { format } from 'date-fns';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PhoneIcon,
  PhoneMissedIcon,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import {
  CallRecordingPlayer,
  type CallRecordingPlayerHandle,
} from './call-recording-player';
import { recordingTimestamp, transcriptOffset } from './call-recording-time';

/** "2:05" / "45s" — how long the caller was on the line. */
export function formatCallDuration(ms: number | null): string | null {
  if (ms === null || ms < 0) return null;
  const total = Math.round(ms / 1000);
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

/** What happened on the call, in one line. */
export function callOutcome(call: ConversationCall): string {
  if (call.id.startsWith('CAdryrun'))
    return 'Simulated call. No phone was rung.';
  if (call.missed && call.transferred)
    return 'The clinic didn’t answer Claire’s transfer.';
  if (call.missed)
    return 'Missed call: nobody was free to answer. Call them back.';
  if (call.status === 'in_progress')
    return call.direction === 'outbound'
      ? 'Calling customer…'
      : 'Call in progress';
  switch (call.attemptOutcome) {
    case 'no_answer':
      return 'Claire tried to call, but the customer didn’t answer.';
    case 'busy':
      return 'Claire tried to call, but the customer’s line was busy.';
    case 'cancelled':
      return 'Claire’s call was cancelled before a conversation started.';
    case 'failed':
      return 'Claire tried to call, but the call couldn’t connect.';
    case 'no_speech':
      return 'The call connected, but Claire didn’t hear a response.';
  }
  if (call.direction === 'outbound' && call.status === 'failed')
    return 'Claire tried to call, but the call couldn’t connect.';
  if (call.status === 'failed') return "The call didn't connect.";
  if (call.booked) return 'Claire booked an appointment on this call.';
  if (call.transferred) return 'The caller was put through to the clinic.';
  if (call.handedOff) return 'Claire passed this call to the team.';
  return call.direction === 'outbound'
    ? 'Claire spoke with the customer on this call.'
    : 'Claire answered this call.';
}

/**
 * A phone call with an AI summary and a transcript opened on request.
 */
export function CallCard({
  call,
  previewLines = [],
}: {
  call: ConversationCall;
  previewLines?: CallTranscriptLine[];
}) {
  const duration = formatCallDuration(call.durationMs);
  const at = call.startedAt;
  const [expanded, setExpanded] = useState(false);
  const live = call.status === 'in_progress' && !call.id.startsWith('CAdryrun');
  const hasConversation =
    !call.missed || call.hasRecording || previewLines.length > 0;
  const attempted =
    call.direction === 'outbound' &&
    !call.id.startsWith('CAdryrun') &&
    !live &&
    (call.status === 'failed' || Boolean(call.attemptOutcome));
  const attemptLabel = {
    no_answer: 'No answer',
    busy: 'Line busy',
    cancelled: 'Cancelled',
    failed: 'Couldn’t connect',
    no_speech: 'No response',
  }[call.attemptOutcome ?? 'failed'];
  const showTranscript = live || expanded;
  const transcriptId = useId();
  const playerRef = useRef<CallRecordingPlayerHandle>(null);
  const transcript = useCallTranscript(
    call.id,
    hasConversation && showTranscript,
    live
  );
  const lines = transcript.data?.lines ?? previewLines;
  const visibleLines = showTranscript ? lines : [];
  const transcriptRef = useRef<HTMLDivElement>(null);
  const followLiveRef = useRef(true);
  useEffect(() => {
    const region = transcriptRef.current;
    if (live && lines.length > 0 && followLiveRef.current && region) {
      region.scrollTop = region.scrollHeight;
    }
  }, [live, lines.length]);

  if (
    attempted &&
    call.status === 'failed' &&
    !call.hasRecording &&
    !previewLines.length
  ) {
    return (
      <section
        aria-label="Phone call"
        className="mx-auto flex w-full max-w-xl items-start gap-3 py-2"
      >
        <span className="flex size-8 shrink-0 items-center justify-center text-muted-foreground">
          <PhoneMissedIcon className="size-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-[13px] font-medium">Claire tried to call</h3>
            {at ? (
              <time
                dateTime={at}
                className="shrink-0 text-xs text-muted-foreground"
              >
                {format(new Date(at), 'h:mm a')}
              </time>
            ) : null}
          </div>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {attemptLabel}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label="Phone call"
      className="mx-auto w-full max-w-xl rounded-xl bg-muted/40 p-4"
    >
      <div className="flex items-start gap-3">
        <span
          className={`flex size-9 shrink-0 items-center justify-center rounded-full ${attempted ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400' : 'bg-muted text-muted-foreground'}`}
        >
          {attempted ? (
            <PhoneMissedIcon className="size-4" aria-hidden />
          ) : (
            <PhoneIcon className="size-4" aria-hidden />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <h3 className="text-sm font-semibold">
              {call.id.startsWith('CAdryrun')
                ? 'Simulated call'
                : call.missed && !hasConversation
                  ? 'Missed call'
                  : attempted
                    ? 'Claire tried to call'
                    : call.direction === 'outbound'
                      ? 'Outgoing call'
                      : 'Incoming call'}
            </h3>
            {at ? (
              <time
                dateTime={at}
                className="shrink-0 text-xs text-muted-foreground"
              >
                {format(new Date(at), 'h:mm a')}
              </time>
            ) : null}
          </div>
          {attempted ? (
            <span className="mt-1 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
              {attemptLabel}
            </span>
          ) : null}
          {duration && (!attempted || call.attemptOutcome === 'no_speech') ? (
            <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
              {duration}
            </p>
          ) : null}
        </div>
      </div>
      <div className={'mt-3'}>
        {!(attempted && call.status === 'failed') ? (
          <div className="mb-2 flex min-h-7 items-center justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {live
                ? 'Live transcript'
                : attempted
                  ? 'Call outcome'
                  : 'AI summary'}
            </span>
            {!live && hasConversation && call.status !== 'failed' ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 gap-1 px-2 text-xs"
                aria-expanded={expanded}
                aria-controls={
                  visibleLines.length > 0 ? transcriptId : undefined
                }
                onClick={() => setExpanded((value) => !value)}
              >
                {expanded ? 'Hide transcript' : 'Show transcript'}
                {expanded ? (
                  <ChevronUpIcon className="size-3" aria-hidden />
                ) : (
                  <ChevronDownIcon className="size-3" aria-hidden />
                )}
              </Button>
            ) : null}
          </div>
        ) : null}
        <p className="text-[13px] leading-[1.65] text-foreground/85">
          {(attempted
            ? callOutcome(call)
            : live
              ? 'Updates as each person finishes speaking.'
              : call.summary) ||
            (call.summaryPending ? 'Summarising the call…' : callOutcome(call))}
        </p>
      </div>
      {showTranscript && visibleLines.length > 0 ? (
        <div className="mt-3 border-t pt-3">
          <div
            id={transcriptId}
            ref={transcriptRef}
            onScroll={(event) => {
              const region = event.currentTarget;
              followLiveRef.current =
                region.scrollHeight - region.scrollTop - region.clientHeight <
                32;
            }}
            role="region"
            aria-label="Call transcript"
            tabIndex={showTranscript ? 0 : undefined}
            className={
              'max-h-80 overflow-y-auto overscroll-contain rounded-md pr-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            }
          >
            <ol className="space-y-3">
              {visibleLines.map((line) => {
                const offset = call.hasRecording
                  ? transcriptOffset(
                      line.at,
                      call.startedAt ?? lines[0]?.at ?? null
                    )
                  : null;
                const content = (
                  <>
                    <span
                      className={`mt-0.5 w-12 shrink-0 rounded px-1.5 py-0.5 text-center text-[11px] font-medium ${line.speaker === 'caller' ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'}`}
                    >
                      {line.speaker === 'caller' ? 'Caller' : 'Claire'}
                    </span>
                    <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-[13px] leading-5">
                      {line.text}
                    </span>
                  </>
                );
                return (
                  <li key={line.id}>
                    {offset !== null ? (
                      <button
                        type="button"
                        aria-label={`Play ${line.speaker === 'caller' ? 'Caller' : 'Claire'} at ${recordingTimestamp(offset)}: ${line.text}`}
                        title={`Play from ${recordingTimestamp(offset)}`}
                        onClick={() => playerRef.current?.seekTo(offset)}
                        className="group -mx-1 flex w-[calc(100%+0.5rem)] items-start gap-2.5 rounded-md p-1 text-left transition-colors hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-blue-950/30"
                      >
                        {content}
                        <span className="mt-1 shrink-0 text-[10px] tabular-nums text-muted-foreground group-hover:text-blue-600 dark:group-hover:text-blue-400">
                          {recordingTimestamp(offset)}
                        </span>
                      </button>
                    ) : (
                      <div className="flex items-start gap-2.5">{content}</div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      ) : null}
      {showTranscript && transcript.isLoading && !previewLines.length ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Loading transcript…
        </p>
      ) : null}
      {showTranscript && transcript.isError ? (
        <p className="mt-2 text-xs text-destructive">
          Could not load the full transcript.
        </p>
      ) : null}
      {call.hasRecording ? (
        <div className="mt-3">
          <CallRecordingPlayer
            ref={playerRef}
            key={call.id}
            callSid={call.id}
            durationMs={call.durationMs}
          />
        </div>
      ) : null}
      {call.missed && call.transferred ? (
        <p className="mt-3 text-xs text-muted-foreground">
          The clinic didn’t answer the transfer.
        </p>
      ) : null}
      {call.recordingExpired ? (
        <p className="mt-1 text-xs text-muted-foreground">
          The recording was deleted after 90 days.
        </p>
      ) : null}
    </section>
  );
}
